import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReviewQueueService from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';
import { CheckCircle, XCircle } from 'react-feather';
import PropTypes from 'prop-types';
import RichTextEditor from '../components/RichTextEditor';
import { portalDefaultHtml } from '../utils/portalCopy';
import '../styles/ReviewQueue.css';

const ReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('raw_data');
  const [showRawJson, setShowRawJson] = useState(false);

  // Editable form state
  const [formData, setFormData] = useState({
    home_top: '',
    home_below: '',
    results_summary: '',
    results_full: '',
    action_plan: '',
  });
  // Grid reports: edited portal HTML per field, only the fields touched since load.
  const [portalEdits, setPortalEdits] = useState({});
  // Bumped on every load so the editors remount with the saved text.
  const [loadCount, setLoadCount] = useState(0);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await ReviewQueueService.getAdminReportDetail(id);
      setReport(res.data);
      setFormData({
        home_top: res.data.home_top || '',
        home_below: res.data.home_below || '',
        results_summary: res.data.results_summary || '',
        results_full: res.data.results_full || '',
        action_plan: res.data.action_plan || '',
      });
      setPortalEdits({});
      setLoadCount((n) => n + 1);
    } catch (e) {
      console.error(e);
      showToast('error', 'Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (report?.copy_source === 'grid') {
      if (Object.keys(portalEdits).length === 0) {
        showToast('info', 'No changes to save');
        return;
      }
      try {
        await ReviewQueueService.savePortalCopy(id, portalEdits);
        showToast('success', 'Changes saved successfully');
        fetchReport();
      } catch (e) {
        showToast('error', 'Failed to save changes');
      }
      return;
    }
    try {
      await ReviewQueueService.updateAdminReport(id, formData);
      showToast('success', 'Changes saved successfully');
      fetchReport();
    } catch (e) {
      showToast('error', 'Failed to save changes');
    }
  };

  // Drops the admin edit for one field, so the patient sees the AI or grid text again.
  const handleResetField = async (field, label) => {
    if (!window.confirm(`Remove the admin edit for ${label}? The patient will see the AI or grid text again.`)) return;
    try {
      await ReviewQueueService.savePortalCopy(id, { [field]: '' });
      showToast('success', `${label} reset`);
      fetchReport();
    } catch (e) {
      showToast('error', 'Failed to reset');
    }
  };

  const handleApprove = async () => {
    try {
      await ReviewQueueService.approveAdminReport(id);
      showToast('success', 'Report Approved & Unlocked!');
      navigate('/admin/review-queue');
    } catch (e) {
      showToast('error', 'Failed to approve report');
    }
  };

  const isGrid = report?.copy_source === 'grid';

  const handleRegenerate = async () => {
    const message = isGrid
      ? 'Re-run the copy grid for this report? Text edited by an admin is kept.'
      : 'Are you sure you want to regenerate this copy? This will overwrite existing AI generated text.';
    if (!window.confirm(message)) return;
    try {
      await ReviewQueueService.regenerateAdminReport(id);
      showToast('info', 'Regeneration triggered. Please wait a moment and refresh.');
      navigate('/admin/review-queue');
    } catch (e) {
      showToast('error', 'Failed to trigger regeneration');
    }
  };

  if (loading) return <div className="review-queue-container">Loading details...</div>;
  if (!report) return <div className="review-queue-container">Report not found.</div>;

  return (
    <div className="review-queue-container review-detail">
      <div className="rq-header flex-wrap gap-2">
        <h1 className="rq-title">Report Review - {report.patient_name || report.order}</h1>
        <div className="d-flex gap-2 flex-wrap">
          <button className="rq-btn-ghost" onClick={() => navigate('/admin/review-queue')}>
            Back
          </button>
          <button className="rq-btn-outline" onClick={handleSave}>
            Save Edits
          </button>
          <button className="rq-btn-warning" onClick={handleRegenerate}>
            {isGrid ? 'Regenerate from Grid' : 'Regenerate AI Copy'}
          </button>
          {!report.unlock_approved && (
            <button className="rq-btn-green" onClick={handleApprove}>
              Approve & Unlock
            </button>
          )}
        </div>
      </div>

      {/* Grid reports have no AI gates: show the copy checks instead. */}
      {isGrid ? (
        <>
          <CopyChecks report={report} />
          <PersonalizationChecks report={report} />
        </>
      ) : (
        <div className="card mb-3">
          <div className="card-header fw-bold">Safety Gates Status</div>
          <div className="card-body d-flex justify-content-around">
            <SafetyBadge label="Clinical Safety" passed={report.clinical_safe} />
            <SafetyBadge label="Legal Safety" passed={report.legally_safe} />
            <SafetyBadge label="Consumer Safety" passed={report.consumer_safe} />
          </div>
          {report.explanation && (
            <div className="card-footer text-danger">
              <strong>Explanation: </strong> {report.explanation}
            </div>
          )}
        </div>
      )}

      <div className="row">
        {/* Left Column - Raw Data & Clinical Context */}
        <div className="col-md-5">
          <div className="card h-100">
            <div className="card-header py-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Step 1 Context (Raw Data)</span>
                {report.ordered_tests && <span className="badge bg-primary">{report.ordered_tests}</span>}
              </div>
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button className={`nav-link fw-bold ${activeTab === 'raw_data' ? 'active' : ''}`} onClick={() => setActiveTab('raw_data')}>
                    Lab Results (Raw)
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link fw-bold ${activeTab === 'intake_data' ? 'active' : ''}`} onClick={() => setActiveTab('intake_data')}>
                    Intake Answers
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link fw-bold ${activeTab === 'classification' ? 'active' : ''}`}
                    onClick={() => setActiveTab('classification')}
                  >
                    Classification
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body py-3" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {activeTab === 'raw_data' && (
                <div>
                  <h6 className="fw-bold mb-3 text-secondary">Extracted Lab Test Values</h6>
                  {renderLabMetrics(report.raw_data, report.pdf_results, showRawJson, setShowRawJson)}
                </div>
              )}
              {activeTab === 'intake_data' && (
                <div>
                  <h6 className="fw-bold mb-3 text-secondary">Patient Questionnaire Answers</h6>
                  {renderIntakeData(report.intake_data)}
                </div>
              )}
              {activeTab === 'classification' && (
                <div>
                  <h6 className="fw-bold">Classification Fields</h6>
                  <ul className="list-group list-group-flush mb-4">
                    <li className="list-group-item px-0">
                      <strong>Pathway:</strong> {report.pathway || 'N/A'}
                    </li>
                    <li className="list-group-item px-0">
                      <strong>Immediate Referral:</strong> {report.immediate_referral ? 'Yes' : 'No'}
                    </li>
                    <li className="list-group-item px-0">
                      <strong>Medication Eligible:</strong> {report.medication_eligible ? 'Yes' : 'No'}
                    </li>
                    <li className="list-group-item px-0">
                      <strong>Hormone Pattern:</strong> {report.hormone_pattern || 'N/A'}
                    </li>
                    <li className="list-group-item px-0">
                      <strong>SwimScore:</strong> {report.swimscore || 'N/A'}
                    </li>
                    <li className="list-group-item px-0">
                      <strong>Hormone Flags:</strong>
                      <pre className="mt-2 rd-inset p-2 rounded">{JSON.stringify(report.hormone_flags, null, 2)}</pre>
                    </li>
                  </ul>
                  <h6 className="fw-bold">Urologist Summary (Internal)</h6>
                  <div className="rd-inset p-3 rounded" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                    {report.urologist_summary || 'Not generated'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Editable AI Copy */}
        <div className="col-md-7">
          <div className="card h-100">
            <div className="card-header fw-bold d-flex justify-content-between py-2">
              <span>Step 2 Patient-Facing Copy (Editable)</span>
            </div>
            {isGrid ? (
              <div className="card-body py-2">
                <PortalCopyEditors
                  report={report}
                  loadCount={loadCount}
                  onEdit={(field, html) => setPortalEdits((prev) => ({ ...prev, [field]: html }))}
                  onReset={handleResetField}
                />
              </div>
            ) : (
              <div className="card-body py-2">
                <div className="mb-3">
                  <label className="form-label fw-bold">Home Top</label>
                  <textarea className="form-control" name="home_top" rows="3" value={formData.home_top} onChange={handleChange}></textarea>
                </div>
                {/* Not rendered by the portal; the copy grid leaves it empty. */}
                {!isGrid && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">Home Below</label>
                    <textarea className="form-control" name="home_below" rows="3" value={formData.home_below} onChange={handleChange}></textarea>
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-bold">Results Summary</label>
                  <textarea
                    className="form-control"
                    name="results_summary"
                    rows="4"
                    value={formData.results_summary}
                    onChange={handleChange}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Results Full</label>
                  <textarea className="form-control" name="results_full" rows="6" value={formData.results_full} onChange={handleChange}></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Action Plan</label>
                  <textarea className="form-control" name="action_plan" rows="5" value={formData.action_plan} onChange={handleChange}></textarea>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const renderLabMetrics = (rawData, pdfResults, showRawJson, setShowRawJson) => {
  if (!rawData || Object.keys(rawData).length === 0) {
    return <div className="text-muted fst-italic">No raw lab metrics available.</div>;
  }

  const categories = rawData.testHistory?.[0]?.categories || rawData.categories || null;

  return (
    <div>
      {categories && typeof categories === 'object' ? (
        <div className="d-flex flex-column gap-3">
          {Object.entries(categories).map(([catKey, cat]) => (
            <div key={catKey} className="border rounded p-2">
              <div className="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2">
                <span className="fw-bold text-primary">{cat.name || catKey}</span>
                {cat.score !== undefined && <span className="badge bg-info">Score: {cat.score}</span>}
              </div>
              {cat.metrics && Array.isArray(cat.metrics) && cat.metrics.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-borderless mb-0" style={{ fontSize: '0.82rem', color: '#ffffff' }}>
                    <thead>
                      <tr className="border-bottom" style={{ color: '#ffffff' }}>
                        <th style={{ color: '#ffffff' }}>Metric</th>
                        <th style={{ color: '#ffffff' }}>Value</th>
                        <th style={{ color: '#ffffff' }}>Ref. Range</th>
                        <th style={{ color: '#ffffff' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cat.metrics.map((m, idx) => {
                        const statusColor =
                          m.status === 'success' ? 'success' : m.status === 'warning' ? 'warning' : m.status === 'danger' ? 'danger' : 'secondary';
                        return (
                          <tr key={idx} className="align-middle border-bottom border-light" style={{ color: '#ffffff' }}>
                            <td className="fw-medium text-white" style={{ color: '#ffffff' }}>
                              {m.name || 'N/A'}
                            </td>
                            <td className="text-white" style={{ color: '#ffffff' }}>
                              <span className="fw-bold text-white" style={{ color: '#ffffff' }}>
                                {m.value || '—'} {m.unit || ''}
                              </span>
                            </td>
                            <td className="text-white" style={{ color: '#ffffff' }}>
                              {m.referenceRange || '—'}
                            </td>
                            <td>{m.status && <span className={`badge bg-${statusColor}`}>{m.status.toUpperCase()}</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted small">No metrics listed</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rd-inset p-3 rounded">
          <pre className="mb-0" style={{ fontSize: '0.85rem', maxHeight: '400px', overflowY: 'auto' }}>
            {JSON.stringify(rawData, null, 2)}
          </pre>
        </div>
      )}

      <div className="mt-3">
        <button className="rq-btn-ghost w-100 d-flex justify-content-between align-items-center" onClick={() => setShowRawJson(!showRawJson)}>
          <span>{showRawJson ? 'Hide' : 'View'} Complete Raw JSON Payload</span>
          <span>{showRawJson ? '▲' : '▼'}</span>
        </button>
        {showRawJson && (
          <div className="mt-2 rd-code p-3 rounded" style={{ maxHeight: '420px', overflowY: 'auto', fontSize: '0.82rem' }}>
            <pre className="mb-0 text-light">{JSON.stringify(rawData, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

const renderIntakeData = (intakeData) => {
  if (!intakeData || Object.keys(intakeData).length === 0) {
    return <div className="text-muted fst-italic">No intake form answers recorded for this order.</div>;
  }

  const entries = Object.entries(intakeData).filter((entry) => entry[1] !== null && entry[1] !== undefined && entry[1] !== '');
  if (entries.length === 0) {
    return <div className="text-muted fst-italic">No intake form answers recorded for this order.</div>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {entries.map(([key, value]) => (
        <div key={key} className="border-bottom pb-2">
          <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>
            {key}
          </div>
          <div className="rd-value fw-medium mt-1" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
            {String(value)}
          </div>
        </div>
      ))}
    </div>
  );
};

const CopyChecks = ({ report }) => {
  const errors = report.copy_validation_errors || [];
  const actionPlan = report.patient_copy?.action_plan;
  const passed = errors.length === 0;
  const rows = [
    ['Grid row', report.copy_row_id],
    ['Status', report.copy_status_key],
    ['Tier', report.copy_tier],
    ['Grid version', report.copy_grid_version],
    ['PMSC source', report.pmsc_source === 'tmsc_fallback' ? 'TMSC (PMSC not reported)' : report.pmsc_source],
    ['Hormone note', report.hormone_note_id],
    ['Referral required (internal)', report.referral_required ? 'Yes' : 'No'],
    ['Sample check flag (internal)', report.sample_check_flag ? 'Yes' : 'No'],
  ];
  return (
    <div className="card mb-3">
      <div className="card-header fw-bold d-flex justify-content-between align-items-center">
        <span>Copy Checks</span>
        <span className={`badge ${passed ? 'bg-success' : 'bg-danger'}`}>{passed ? 'Passed' : 'Failed'}</span>
      </div>
      <div className="card-body py-2">
        <div className="row">
          {rows.map(([label, value]) => (
            <div className="col-6 col-md-3 py-1" key={label}>
              <small className="text-muted d-block">{label}</small>
              <span className="fw-bold">{value || 'N/A'}</span>
            </div>
          ))}
        </div>
        {actionPlan && (
          <div className="mt-2">
            <small className="text-muted d-block">Action plan cards (from the grid rules)</small>
            <span className="fw-bold">{actionPlan.codes.join(' · ')}</span>
            <small className="text-muted d-block mt-1">The patient sees these unless an admin override is set in Patients, Next Step Cards.</small>
          </div>
        )}
        {!passed && (
          <ul className="text-danger mb-0 mt-2">
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
        {report.copy_edited_by_admin && (
          <div className="alert alert-info py-1 px-2 mt-2 mb-0">Edited by an admin. Regenerating keeps the edited text.</div>
        )}
      </div>
      {report.explanation && (
        <div className={`card-footer ${passed ? 'text-muted' : 'text-danger'}`}>
          <strong>Explanation: </strong> {report.explanation}
        </div>
      )}
    </div>
  );
};

CopyChecks.propTypes = {
  report: PropTypes.object.isRequired,
};

const PERSONALIZATION_STATUS = {
  personalized: { label: 'All 4 fields personalized', badge: 'bg-success' },
  partial: { label: 'Partly personalized', badge: 'bg-warning text-dark' },
  failed: { label: 'Failed, grid text shown', badge: 'bg-danger' },
  skipped: { label: 'Skipped', badge: 'bg-secondary' },
};

const PERSONALIZATION_FIELD_LABELS = {
  home_top: 'Home Top',
  results_summary: 'Results Summary',
  results_full: 'Results Full',
  action_plan: 'Action Plan',
  model: 'Model call',
};

// AI personalization of the grid copy with the intake answers. A field that
// failed a backend check shows the grid text to the patient.
const PersonalizationChecks = ({ report }) => {
  const [showRaw, setShowRaw] = useState(false);
  const status = PERSONALIZATION_STATUS[report.personalization_status] || { label: 'Not run', badge: 'bg-secondary' };
  const errors = Object.entries(report.personalization_errors || {});
  const raw = report.personalization_raw;
  return (
    <div className="card mb-3">
      <div className="card-header fw-bold d-flex justify-content-between align-items-center">
        <span>AI Personalization</span>
        <span className={`badge ${status.badge}`}>{status.label}</span>
      </div>
      <div className="card-body py-2">
        <small className="text-muted d-block">
          The patient sees the AI text for each field that passed every check, and the grid text for the rest. An admin edit (below, or while
          impersonating the patient) shows instead of both and is kept on regenerate.
        </small>
        {report.personalization_status === 'skipped' && (
          <div className="mt-2">Not run: the copy was edited by an admin, or there is no grid copy.</div>
        )}
        {errors.length > 0 && (
          <div className="mt-2">
            <small className="text-muted d-block">Fields that fell back to the grid text</small>
            <ul className="text-danger mb-0">
              {errors.map(([field, messages]) => (
                <li key={field}>
                  <strong>{PERSONALIZATION_FIELD_LABELS[field] || field}:</strong> {messages.join('; ')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {raw && (
          <div className="mt-2">
            <button className="rq-btn-ghost" onClick={() => setShowRaw(!showRaw)}>
              {showRaw ? 'Hide' : 'View'} raw model output
            </button>
            {showRaw && (
              <div className="mt-2 rd-code p-3 rounded" style={{ maxHeight: '420px', overflowY: 'auto', fontSize: '0.82rem' }}>
                <div className="mb-2 text-muted">Model: {raw.model_id}</div>
                <RawModelOutput output={raw.output} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// The model returns a JSON object of field -> text. Show each field as its own
// block with real line breaks; anything that is not JSON shows as is.
const parseModelOutput = (output) => {
  if (!output) return null;
  const text = output
    .trim()
    .replace(/^```(?:json)?\s*/, '')
    .replace(/```$/, '');
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const RawModelOutput = ({ output }) => {
  const fields = parseModelOutput(output);
  if (!fields) {
    return (
      <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
        {output || 'No output'}
      </pre>
    );
  }
  return (
    <div className="rd-raw-fields">
      {Object.entries(fields).map(([key, value]) => (
        <div className="rd-raw-field" key={key}>
          <div className="rd-raw-key">{key}</div>
          <div className="rd-raw-value">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</div>
        </div>
      ))}
    </div>
  );
};

RawModelOutput.propTypes = {
  output: PropTypes.string,
};

PersonalizationChecks.propTypes = {
  report: PropTypes.object.isRequired,
};

const PORTAL_FIELDS = [
  ['home_top', 'Home Top'],
  ['results_summary', 'Results Summary'],
  ['results_full', 'Results Full'],
  ['action_plan', 'Action Plan Summary'],
];

// Where the text the patient sees for one field comes from.
const fieldSource = (report, field) => {
  if (report.portal_overrides?.[field]) return { label: 'Admin edit', badge: 'bg-primary' };
  if (report.copy_edited_by_admin) return { label: 'Edited plain text', badge: 'bg-secondary' };
  if (report[`${field}_personalized`]) return { label: 'AI personalized', badge: 'bg-success' };
  return { label: 'Grid', badge: 'bg-secondary' };
};

// Grid reports: edit the copy exactly as the patient portal shows it. Saves go
// to the same place as edits made while impersonating the patient.
const PortalCopyEditors = ({ report, loadCount, onEdit, onReset }) => {
  const defaults = portalDefaultHtml(report);
  return (
    <>
      <small className="text-muted d-block mb-2">
        This is what the patient sees. Saving stores your text as an admin edit (the same as editing while impersonating the patient); the grid and AI
        text stay underneath, and Reset brings them back.
      </small>
      {PORTAL_FIELDS.map(([field, label]) => {
        const source = fieldSource(report, field);
        const edited = !!report.portal_overrides?.[field];
        return (
          <div className="mb-3" key={field}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label fw-bold mb-0">
                {label} <span className={`badge ${source.badge} ms-1`}>{source.label}</span>
              </label>
              {edited && (
                <button type="button" className="rq-link-btn" onClick={() => onReset(field, label)}>
                  Reset to AI / grid text
                </button>
              )}
            </div>
            <RichTextEditor
              key={`${field}-${loadCount}`}
              initialValue={report.portal_overrides?.[field] || defaults[field]}
              onChange={(html) => onEdit(field, html)}
            />
          </div>
        );
      })}
    </>
  );
};

PortalCopyEditors.propTypes = {
  report: PropTypes.object.isRequired,
  loadCount: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

const SafetyBadge = ({ label, passed }) => {
  if (passed === null || passed === undefined) {
    return (
      <div className="text-center">
        <div className="text-muted mb-1">
          <XCircle size={32} />
        </div>
        <small className="fw-bold text-muted">{label}</small>
        <br />
        <span className="badge bg-secondary">Pending</span>
      </div>
    );
  }
  return (
    <div className="text-center">
      <div className={passed ? 'text-success mb-1' : 'text-danger mb-1'}>{passed ? <CheckCircle size={32} /> : <XCircle size={32} />}</div>
      <small className="fw-bold">{label}</small>
      <br />
      <span className={`badge ${passed ? 'bg-success' : 'bg-danger'}`}>{passed ? 'Passed' : 'Failed'}</span>
    </div>
  );
};

SafetyBadge.propTypes = {
  label: PropTypes.string.isRequired,
  passed: PropTypes.bool,
};

export default ReviewDetail;
